import { useMutation, useQuery } from '@apollo/client';
import { useCallback, useMemo } from 'react';

import {
	DEFAULT_FROM_TIME_VALUE,
	MAX_DEFAULT_TILL_TIME_VALUE,
	MIN_DEFAULT_TILL_TIME_VALUE,
} from 'common/activeElements/modals/hooks/const';
import { useFormWithTimeInputs } from 'common/activeElements/modals/hooks/useFormWithTimeInput';
import { mapOverlappingActivitiesErrors } from 'common/OverlappingActivitiesBanner/helpers/overlappingActivities';
import useOverlappingBanner from 'common/OverlappingActivitiesBanner/hooks/useOverlappingBanner';
import { standardError, standardSuccess } from 'global/commonMessages';
import { notificationStore } from 'global/NotificationStore';
import { FETCH_UNAVAILABITY_DAYS } from 'pages/CrewMemberDetails/components/Unavailability/unavailalbityQuery.gql';
import { resetTimestampToUtc, reverseFormTime, updateDateWithTimeAndTZ } from 'utils/datetime';

import {
	CREW_MEMBER_ABSENCE_CONFLICT_WA_RESPONSE,
	CREW_MEMBER_ABSENCE_ERROR_RESPONSE,
	CREW_MEMBER_ABSENCE_OVERLAPPING_ACTIVITIES_RESPONSE,
	CREW_MEMBER_ABSENCE_SUCCESS_RESPONSE,
} from './const';
import { CREATE_CREW_MEMBER_ABSENCE } from './qql/createCrewMemberAbsence';
import { FETCH_REASONS } from './qql/fetchReasons';
import { UPDATE_CREW_MEMBER_ABSENCE } from './qql/updateCrewMemberAbsence';

const DEFAULT_VALUES = {
	from: '',
	till: '',
	fromTime: '',
	tillTime: '',
	reason: '',
	note: '',
};

export const useModalAbsence = ({
	employeeId,
	baseTimezone,
	readOnly,
	isEditMode,
	initialData,
	onActionSuccess,
	onClose,
}) => {
	const {
		hasOverlappingActivities,
		cannotCancelOverlaps,
		overlappingActivities,
		overlappingAction,
		allowToDeleteOverlaps,
		setOverlappingAction,
		setOverlappingActivities,
		resetOverlappingActivities,
	} = useOverlappingBanner(baseTimezone);

	const { loading: reasonsLoading, data: reasonsData } = useQuery(FETCH_REASONS);

	const initialFormValues = useMemo(() => {
		if (!readOnly && !isEditMode) return DEFAULT_VALUES;

		if (!initialData) return DEFAULT_VALUES;

		const time = reverseFormTime(initialData.fromTimestamp, initialData.toTimestamp, baseTimezone);

		return {
			...DEFAULT_VALUES,
			...time,
			reason: initialData?.reason,
			note: initialData?.note ?? '',
		};
	}, [readOnly, isEditMode, initialData, baseTimezone]);

	const {
		miDay,
		isDirty,
		isValid,
		control,
		defineMinDay,
		isValidDate,
		validateYear,
		handleResetForm,
		validateTillTime,
		reset,
		setValue,
		getValues,
		trigger,
		watch,
	} = useFormWithTimeInputs(initialFormValues);

	const resetOverlapsOnChange = useCallback(() => {
		resetOverlappingActivities();
	}, [resetOverlappingActivities]);

	const handleClose = useCallback(() => {
		handleResetForm();
		resetOverlappingActivities();
		onClose();
	}, [handleResetForm, resetOverlappingActivities, onClose]);

	const handleAbsenceResponseErrors = useCallback(
		(notificationMsg = standardError) => {
			notificationStore.setNotification(notificationMsg);
			reset(DEFAULT_VALUES);
			resetOverlappingActivities();
		},
		[reset, resetOverlappingActivities]
	);

	const buildAbsenceInput = useCallback(() => {
		const { from, till, fromTime, tillTime, reason, note } = getValues();
		const newFrom = updateDateWithTimeAndTZ(from, fromTime, baseTimezone);
		const newTo = updateDateWithTimeAndTZ(till, tillTime, baseTimezone);

		return {
			crewMemberId: employeeId,
			reasonId: reason,
			fromTimestamp: resetTimestampToUtc(newFrom),
			toTimestamp: resetTimestampToUtc(newTo),
			...(note ? { note } : {}),
			...(isEditMode && typeof initialData?.availableForAssignments === 'boolean'
				? { availableForAssignments: initialData.availableForAssignments }
				: {}),
		};
	}, [getValues, employeeId, baseTimezone, isEditMode, initialData]);

	const handleAbsenceSuccess = useCallback(
		(response) => {
			handleResetForm();
			notificationStore.setNotification(standardSuccess);
			setOverlappingActivities([]);
			onActionSuccess(response.absence.fromTimestamp);
			handleClose();
		},
		[handleResetForm, setOverlappingActivities, onActionSuccess, handleClose]
	);

	const handleAbsenceError = useCallback(() => {
		notificationStore.setNotification(standardError);
		reset(DEFAULT_VALUES);
	}, [reset]);

	const handleCreateAbsenceCompleted = (payload) => {
		const response = payload?.createCrewMemberAbsence;

		if (response.__typename === CREW_MEMBER_ABSENCE_SUCCESS_RESPONSE) {
			handleAbsenceSuccess(response);
		}

		if (response.__typename === CREW_MEMBER_ABSENCE_CONFLICT_WA_RESPONSE) {
			const input = buildAbsenceInput();

			createAbsence({ variables: { input, overlappingActivityAction: 'cancel' } });
		}

		if (response.__typename === CREW_MEMBER_ABSENCE_ERROR_RESPONSE) {
			handleAbsenceResponseErrors(mapOverlappingActivitiesErrors(response.errors));
		}

		if (response.__typename === CREW_MEMBER_ABSENCE_OVERLAPPING_ACTIVITIES_RESPONSE) {
			setOverlappingActivities(response.overlappingActivities);
		}
	};

	const [createAbsence, { loading: createLoading }] = useMutation(CREATE_CREW_MEMBER_ABSENCE, {
		notifyOnNetworkStatusChange: true,
		onCompleted: handleCreateAbsenceCompleted,
		onError: handleAbsenceError,
		fetchPolicy: 'network-only',
		refetchQueries: [FETCH_UNAVAILABITY_DAYS],
	});

	const handleUpdateAbsenceCompleted = (payload) => {
		const response = payload?.updateCrewMemberAbsence;

		if (response.__typename === CREW_MEMBER_ABSENCE_SUCCESS_RESPONSE) {
			handleAbsenceSuccess(response);
		}

		if (response.__typename === CREW_MEMBER_ABSENCE_CONFLICT_WA_RESPONSE) {
			const input = buildAbsenceInput();

			updateAbsence({
				variables: { id: initialData.id, input, overlappingActivityAction: 'cancel' },
			});
		}

		if (response.__typename === CREW_MEMBER_ABSENCE_ERROR_RESPONSE) {
			handleAbsenceResponseErrors(mapOverlappingActivitiesErrors(response.errors));
		}

		if (response.__typename === CREW_MEMBER_ABSENCE_OVERLAPPING_ACTIVITIES_RESPONSE) {
			setOverlappingActivities(response.overlappingActivities);
		}
	};

	const [updateAbsence, { loading: updateLoading }] = useMutation(UPDATE_CREW_MEMBER_ABSENCE, {
		notifyOnNetworkStatusChange: true,
		onCompleted: handleUpdateAbsenceCompleted,
		onError: handleAbsenceError,
		fetchPolicy: 'network-only',
		refetchQueries: [FETCH_UNAVAILABITY_DAYS],
	});

	const handleSubmit = useCallback(() => {
		const input = buildAbsenceInput();
		const overlappingActivityAction = allowToDeleteOverlaps ? 'cancel' : null;

		if (isEditMode) {
			updateAbsence({
				variables: {
					id: initialData.id,
					input,
					overlappingActivityAction,
				},
			});
			return;
		}

		createAbsence({
			variables: {
				input,
				overlappingActivityAction,
			},
		});
	}, [
		buildAbsenceInput,
		isEditMode,
		initialData,
		allowToDeleteOverlaps,
		createAbsence,
		updateAbsence,
	]);

	const handleFromDateChange = useCallback(
		(field, e) => {
			const newFromDate = e.target.value;
			const currentTill = getValues().till;

			field.onChange(newFromDate);
			setValue('fromTime', DEFAULT_FROM_TIME_VALUE, { shouldDirty: true, shouldValidate: true });

			if (currentTill && newFromDate > currentTill) {
				setValue('till', newFromDate, { shouldDirty: true, shouldValidate: true });
			}

			if (currentTill) trigger('till');
			defineMinDay();
			resetOverlapsOnChange();
		},
		[getValues, setValue, trigger, defineMinDay, resetOverlapsOnChange]
	);

	const handleFromTimeChange = useCallback(
		(field, e) => {
			field.onChange(e.target.value);
			trigger('tillTime');
			resetOverlapsOnChange();
		},
		[trigger, resetOverlapsOnChange]
	);

	const handleTillDateChange = useCallback(
		(field, e) => {
			field.onChange(e.target.value);

			if (!getValues().tillTime) {
				setValue('tillTime', MAX_DEFAULT_TILL_TIME_VALUE, {
					shouldDirty: true,
					shouldValidate: true,
				});
			} else {
				trigger('tillTime');
			}

			trigger('from');
			resetOverlapsOnChange();
		},
		[getValues, setValue, trigger, resetOverlapsOnChange]
	);

	const handleTillTimeChange = useCallback(
		(field, e) => {
			field.onChange(e.target.value);
			resetOverlapsOnChange();
		},
		[resetOverlapsOnChange]
	);

	const from = watch('from');
	const fromTime = watch('fromTime');
	const till = watch('till');

	const minTillTime = useMemo(() => {
		if (from && fromTime && till && from === till) {
			const start = new Date(`${from}T${fromTime}`);

			start.setMinutes(start.getMinutes() + 1);

			return start.toTimeString().slice(0, 5);
		}

		return MIN_DEFAULT_TILL_TIME_VALUE;
	}, [from, fromTime, till]);

	const isFromTimeDisabled = !from;
	const isTillDisabled = !from || !fromTime;
	const isTillTimeDisabled = !from || !fromTime || !till;

	const isSubmitDisabled = useMemo(
		() =>
			!isDirty ||
			!isValid ||
			(hasOverlappingActivities && !allowToDeleteOverlaps) ||
			(hasOverlappingActivities && cannotCancelOverlaps),
		[isDirty, isValid, hasOverlappingActivities, allowToDeleteOverlaps, cannotCancelOverlaps]
	);

	const reasons = reasonsData?.crewMemberAbsenceReasons ?? [];

	return {
		control,
		miDay,
		minTillTime,
		maxTillTime: MAX_DEFAULT_TILL_TIME_VALUE,
		loading: createLoading || updateLoading,
		reasonsLoading,
		reasons,
		isSubmitDisabled,
		isFromTimeDisabled,
		isTillDisabled,
		isTillTimeDisabled,
		hasOverlappingActivities,
		overlappingActivities,
		overlappingAction,
		cannotCancelOverlaps,
		isValidDate,
		validateYear,
		validateTillTime,
		handleFromDateChange,
		handleFromTimeChange,
		handleTillDateChange,
		handleTillTimeChange,
		setOverlappingAction,
		handleSubmit,
		handleClose,
	};
};
